import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import SupportRequestType from '@/models/common/SupportRequestType';
import SupportRequestTypeFilter from './filters/SupportRequestTypeFilter';
import DateFilter from './filters/DateFilter';
import FundFilter from '@/components/filters/FundFilter/FundFilter';
import BeneficiaryFilter from './filters/BeneficiaryFilter';
import SupplierFilter from './filters/SupplierFilter';
import ThirdPartyPaymentFilter from './filters/ThirdPartyPaymentFilter';
import PageSizeSelector from '@/components/PageSizeSelector/PageSizeSelector';
import Button from '@/components/ui/Button';
import styles from '@/components/filters/FiltersArea.module.scss';

interface FiltersAreaProps {
  selectedTypes: SupportRequestType[];
  setSelectedTypes: Dispatch<SetStateAction<SupportRequestType[]>>;
  dateRange: { from: string; to: string } | null;
  setDateRange: Dispatch<SetStateAction<{ from: string; to: string } | null>>;
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  onRefreshBalances: () => void;
  isRefreshingBalances: boolean;
  selectedFundId: number | null;
  onFundChange: (fundId: number | null) => void;
  selectedBeneficiaryContactId: number | null;
  onBeneficiaryChange: (id: number | null) => void;
  selectedSupplierId: number | null;
  isThirdPartyPayment: boolean | undefined;
  onThirdPartyPaymentChange: (value: boolean | undefined) => void;
  onSupplierChange: (id: number | null) => void;
  /** SPR-1212: העמוד באמצע refetch — מועבר ל-DateFilter → OptionsRangeFilter. */
  isBusy?: boolean;
}

const FiltersArea = ({
  selectedTypes,
  setSelectedTypes,
  dateRange,
  setDateRange,
  pageSize,
  onPageSizeChange,
  onRefreshBalances,
  isRefreshingBalances,
  selectedFundId,
  onFundChange,
  selectedBeneficiaryContactId,
  onBeneficiaryChange,
  selectedSupplierId,
  isThirdPartyPayment,
  onThirdPartyPaymentChange,
  onSupplierChange,
  isBusy,
}: FiltersAreaProps) => {
  const { t } = useTranslation();

  const handleTypeFilterChange = (types: SupportRequestType[]) => {
    setSelectedTypes(types);
  };

  const handleDateFilterChange = (range: { from: string; to: string } | null) => {
    setDateRange(range);
  };

  return (
    <div className={styles.filtersArea}>
      <div className={styles.filtersContainer}>
        <DateFilter 
          dateRange={dateRange}
          onDateRangeChange={handleDateFilterChange}
          isBusy={isBusy}
        />
        <SupportRequestTypeFilter 
          selectedTypes={selectedTypes}
          onTypesChange={handleTypeFilterChange}
        />        
        <FundFilter
          selectedFundId={selectedFundId}
          onFundChange={onFundChange}
        />
        <BeneficiaryFilter
          selectedBeneficiaryContactId={selectedBeneficiaryContactId}
          onBeneficiaryChange={onBeneficiaryChange}
        />
        <SupplierFilter
          selectedSupplierId={selectedSupplierId}
          onSupplierChange={onSupplierChange}
        />
        <ThirdPartyPaymentFilter
          value={isThirdPartyPayment}
          onChange={onThirdPartyPaymentChange}
        />
        <Button
          onClick={onRefreshBalances}
          disabled={isRefreshingBalances}
          variant="primary"
          className="flex items-center !h-8 !rounded-[24px]"
        >
          {isRefreshingBalances && (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin ml-2" />
          )}
          {isRefreshingBalances ? t('refreshingBalances') : t('refreshBalances')}
        </Button>
      </div>
      <div className={styles.controlsContainer}>
        <PageSizeSelector 
          value={pageSize} 
          onChange={onPageSizeChange}
        />
      </div>
    </div>
  );
};

export default FiltersArea;
